package com.nonopn.skydome.common.app.pages.keraunic

import kotlinx.serialization.Serializable

/**
 * Partial model for the /skydome/report/keraunic JSON response.
 * Only the fields needed for zone polarity stats, daily/hourly breakdown
 * and individual activities are modelled — unknown keys are ignored.
 */
@Serializable
data class ApiKeraunicDetail(
    val eventsAt1Km: ApiKeraunicEvents = ApiKeraunicEvents(),
    val eventsAt2Km: ApiKeraunicEvents = ApiKeraunicEvents(),
    val graph: ApiKeraunicGraph? = null,
    val activities: List<ApiKeraunicActivity> = emptyList(),
)

@Serializable
data class ApiKeraunicEvents(
    val pos: ApiKeraunicEventStats = ApiKeraunicEventStats(),
    val negs: ApiKeraunicEventStats = ApiKeraunicEventStats(),
)

@Serializable
data class ApiKeraunicEventStats(
    val count: Int = 0,
    val avg: Double = 0.0,
    val median: Int = 0,
    val max: Int = 0,
)

@Serializable
data class ApiKeraunicGraph(
    val hours: ApiKeraunicHoursSection? = null,
    val days: ApiKeraunicDaysSection? = null,
)

@Serializable
data class ApiKeraunicHoursSection(
    val hours: List<ApiKeraunicTimeBucket> = emptyList(),
)

@Serializable
data class ApiKeraunicDaysSection(
    val numberDaysLightningActivity: Int = 0,
    val days: List<ApiKeraunicTimeBucket> = emptyList(),
)

@Serializable
data class ApiKeraunicTimeBucket(
    val title: String = "",
    val positif: ApiKeraunicBucketStats = ApiKeraunicBucketStats(),
    val negatif: ApiKeraunicBucketStats = ApiKeraunicBucketStats(),
)

@Serializable
data class ApiKeraunicBucketStats(
    val count: Int = 0,
    val max: Int = 0,
    val average: Double = 0.0,
    val median: Int = 0,
)

/**
 * Individual lightning event returned in the keraunicDetail response.
 * Since November 2026 the server only returns the flashes within 2 km (activities = eventsAt1Km + eventsAt2Km),
 * each with its time, type and distance. Older responses only had lat/lon + peak amplitude.
 */
@Serializable
data class ApiKeraunicActivity(
    val latitude: Double = 0.0,
    val longitude: Double = 0.0,
    // Signed peak current in amperes (negative for a negative flash)
    val pulsePeakAmp: Int = 0,
    // Time of the flash (UTC seconds) and its id (may be empty when unknown)
    val atSecondsUtc: Long? = null,
    val id: String? = null,
    // "cg" (cloud-to-ground) or "ic" (intra-cloud)
    val type: String? = null,
    // Distance to the requested point, computed by the server
    val distanceKm: Double? = null,
) {
    val isCloudToGround: Boolean get() = type.equals("cg", ignoreCase = true)
}
